import { useMutation, useQuery } from "react-query";
import { faireApi } from "../../services/axios/api/faireApi";
import { FaireProductParams, faireStoreDetailsParam } from "../../services/models/types/faire.types";
import { showToast } from "@wix/dashboard-sdk";
import React, { useState } from "react";
import WixConfig from "../../config/wix";
import { addCategoryAndTransform } from "../../services/utils/faireUtils";
import { useDebouncedCallback } from "use-debounce";
import FaireProduct from "../../models/faire-product";
import GetRequest from "../../utilities/api/get-request";
import { authAxios } from "../../services/axios/default";

/**
 * The `useFaireProducts` function is a custom hook that fetches Faire products based on various
 * parameters and returns a query hook that fetches Faire Products from an API called faireApi.
 */
export function useFaireProducts({
  page = 1,
  perPage = 10,
  status,
  keywords,
  categoryId,
  exportStatus = "",
}: FaireProductParams) {
  return useQuery(
    ["faire-products", page, status, keywords, categoryId, perPage, exportStatus],
    () => faireApi.fetchProducts({ page, status, keywords, categoryId, perPage, exportStatus }),
    {
      retry: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );
}

/**
 * The function `useFaireProductsCount` returns a query hook that fetches Faire Products Count from an API
 * called `faireApi` to fetch the count of Faire orders.
 */
export function useFaireProductsCount() {
  return useQuery(["faire-products-count"], () => faireApi.fetchProductsCount(), {
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
}

/**
 * The function `useFaireOrdersCount` returns a query hook that fetches Faire Orders Count from an API
 * called `faireApi` to fetch the count of Faire orders.
 */
export function useFaireOrdersCount() {
  return useQuery(["faire-orders-count"], () => faireApi.fetchFaireOrdersCount(), {
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
}

/**
 * The function `useFaireCategories` returns a query hook that fetches Faire categories from an API and
 * provides options for retrying, refetching, and keeping previous data.
 */
export function useFaireCategories({
  page = 1,
  perPage = 30,
  keywords,
}: {
  page: number;
  perPage: number;
  keywords: string;
}) {
  return useQuery(
    ["faire-categories", page, perPage, keywords],
    () => faireApi.fetchFaireCategories({ page, perPage, keywords }),
    {
      retry: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );
}

/**
 * The `useFaireProductImport` function is a custom hook that handles importing Faire products and
 * manages the state of the import process.
 * @returns The function `useFaireProductImport` returns an object with three properties:
 */
export function useFaireProductImport() {
  const [isImportingFaireProducts, setIsImportingFaireProducts] = useState(false);

  const importFaireProductMutation = useMutation((data) => faireApi.importProducts(data), {
    onSuccess: () => {
      showToast({
        type: "success",
        message: "Your Faire products are being imported.",
      });
    },
    onError: () => {
      showToast({
        type: "error",
        message: "Failed to import products. Please try again or contact support.",
      });
    },
    onSettled: () => {
      setIsImportingFaireProducts(false);
    },
  });

  const importFaireProducts = () => {
    if (isImportingFaireProducts) return;
    setIsImportingFaireProducts(true);
    importFaireProductMutation.mutate();
  };

  return { importFaireProducts, isImportingFaireProducts, setIsImportingFaireProducts };
}

/**
 * The `useFaireProductBulkExport` function is a custom hook that handles bulk exporting of Faire
 * products, including showing export confirmation, making a bulk export mutation, and handling success
 * and error messages.
 */
export function useFaireProductBulkExport({ faireProductsQuery, setEmptySelectionItem, productIds }: any) {
  const [showExportConfirmation, setShowExportConfirmation] = useState(false);

  const BulkExportMutation = useMutation(() => faireApi.bulkUploadToWix(productIds), {
    onSuccess: (response) => {
      showToast({
        type: "success",
        message: response.message,
      });
      setEmptySelectionItem();
    },
    onError: (error: any) => {
      showToast({
        type: "error",
        message: error.message,
      });
    },
    onSettled: () => {
      faireProductsQuery.refetch();
    },
  });

  return { BulkExportMutation, showExportConfirmation, setShowExportConfirmation };
}

/**
 * The `useCategoryFetching` function is a custom hook that handles fetching and managing category
 * data, including pagination and debouncing search queries.
 * @returns The `useCategoryFetching` function returns an object with the following properties:
 */
const useCategoryFetching = () => {
  const perPage = 30;
  const [keywords, setKeywords] = useState<string>("");
  const [debouncedKeywords, setDebouncedKeywords] = useState<string>("");
  const [currentCategoryPageNumber, setCurrentCategoryPageNumber] = useState(1);
  const [categoryData, setCategoryData] = useState<any>([]);
  const [hasMore, setHasMore] = useState(true);

  const debouncedCategory = useDebouncedCallback((value) => setDebouncedKeywords(value), 1000);

  const faireCategoryQuery = useFaireCategories({
    page: currentCategoryPageNumber,
    perPage: perPage,
    keywords: debouncedKeywords,
  });

  // importProducts: async (data?: any): Promise<any> => {
  //   return authAxios.post("/api/products/faire/import", data);
  // },

  const fetchCategories = async () => {
    await authAxios
      .get(
        `${WixConfig.backendApiBaseUrl}/api/taxonomies/faire?perPage=${perPage}&keywords=${debouncedKeywords}&page=${
          currentCategoryPageNumber + 1
        }`
      )
      .then((response: any) => {
        setCategoryData([...categoryData, ...response.data]);
        setCurrentCategoryPageNumber(response.current_page);
        setHasMore(response.next_page_url !== null);
      });
  };

  const searchCategories = async () => {
    await authAxios
      .get(
        `${WixConfig.backendApiBaseUrl}/api/taxonomies/faire?perPage=${perPage}&keywords=${debouncedKeywords}&page=1`
      )
      .then((response: any) => {
        setCategoryData([...response.data]);
        setCurrentCategoryPageNumber(response.current_page);
        setHasMore(response.next_page_url !== null);
      });
  };

  const faireCategoryOptions = faireCategoryQuery.isSuccess ? addCategoryAndTransform(categoryData) : [];

  React.useEffect(() => {
    searchCategories();
  }, [debouncedKeywords]);

  return {
    keywords,
    categoryData,
    setKeywords,
    fetchCategories,
    hasMore,
    debouncedCategory,
    setDebouncedKeywords,
    faireCategoryOptions,
    faireCategoryQuery,
  };
};

export default useCategoryFetching;

/**
 * The `useFaireStoreDetails` function is a custom hook in TypeScript that fetches Faire store details
 * and returns the fetched data and loading state.
 * @param {faireStoreDetailsParam} initialData - The initialData parameter is the initial value for the
 * faireData state.
 * @returns The `useFaireStoreDetails` function returns an object with two properties: `faireData` and
 * `isLoading`.
 */
export function useFaireStoreDetails(initialData: faireStoreDetailsParam) {
  const [faireData, setFaireData] = useState<faireStoreDetailsParam>(initialData);
  const [isLoading, setLoading] = useState(false);

  const fetchFaireData = () => {
    if (isLoading) return;
    setLoading(true);

    FaireProduct.getFaireStoreDetails()
      .then((response: any) => {
        setFaireData(response?.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    fetchFaireData();
  }, []);

  return { faireData, isLoading };
}
